import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  LinearProgress,
  Badge,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useUser } from '../contexts/UserContext';
import { database, storage } from '../firebase';
import { ref, push, set, onValue, get, update } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useEvents } from '../contexts/EventsContext';
import { useSchools } from '../contexts/SchoolsContext';
import { usePrograms } from '../contexts/ProgramsContext';
import { findSchoolIdByName } from '../utils/schoolUtils';
import { parseMessageContentAsHTML } from '../utils/messageParser';
import '../components/edvisor/EdvisorMessage.css';
import Reports from './Reports';

interface SupportTicketEntry {
  supportTicketID: string;
  clientUserID: string;
  responderUserID?: string;
  timestamp: number;
  comment: string;
  status: string;
  id?: string;
}

interface GroupedTicket {
  supportTicketID: string;
  entries: SupportTicketEntry[];
  clientUserID: string;
  latestStatus: string;
  latestTimestamp: number;
  firstComment: string;
}

interface GuidanceUser {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  highSchool: string;
  city: string;
  province: string;
  selfClaimedGuidance: boolean;
}

interface HighSchool {
  id: string;
  name: string;
}

interface ApprovalFormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  highSchool: HighSchool | null;
  avatarFile: File | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`guidance-tabpanel-${index}`}
      aria-labelledby={`guidance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `guidance-tab-${index}`,
    'aria-controls': `guidance-tabpanel-${index}`,
  };
}

const AdminSupport: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: userData } = useUser();
  const { schools } = useSchools();
  const { programs } = usePrograms();
  const [currentTab, setCurrentTab] = useState(0);
  const [tickets, setTickets] = useState<GroupedTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<GroupedTicket | null>(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseComment, setResponseComment] = useState('');
  const [responseStatus, setResponseStatus] = useState('Resolution in Process');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [userDataMap, setUserDataMap] = useState<Record<string, { firstName?: string; lastName?: string; email?: string }>>({});

  // Filters
  const [userIdFilter, setUserIdFilter] = useState('');
  const [ticketIdFilter, setTicketIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Show All');

  // Events tab state
  const { events, loading: eventsLoading, error: eventsError, refresh: refreshEvents } = useEvents();
  const [selectedHost, setSelectedHost] = useState<string>('');
  const [filterMode, setFilterMode] = useState<'future' | 'all'>('future');
  const [aiSearchInProgress, setAiSearchInProgress] = useState(false);
  const [searchProgress, setSearchProgress] = useState<any>(null);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [searchSummary, setSearchSummary] = useState<any>(null);
  const [aiGeneratedEvents, setAiGeneratedEvents] = useState<any[]>([]);
  const [schoolsWithDrafts, setSchoolsWithDrafts] = useState<any[]>([]);
  const [selectedDraftSchool, setSelectedDraftSchool] = useState<string>('');
  const [aiEventsLoading, setAiEventsLoading] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['pending', 'approved', 'published']);
  const [selectedDraftTabSchool, setSelectedDraftTabSchool] = useState<string>('');
  const [eventsTabIndex, setEventsTabIndex] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    location: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    website: '',
    registerRequired: false,
    eventType: 'physical',
    private: false,
    hostName: '',
    eventCategory: ['General'] as string[]
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Guidance tab state
  const [guidanceTabIndex, setGuidanceTabIndex] = useState(0);
  const [guidanceLoading, setGuidanceLoading] = useState(true);
  const [guidanceError, setGuidanceError] = useState<string | null>(null);
  const [pendingUsers, setPendingUsers] = useState<GuidanceUser[]>([]);
  const [activeUsers, setActiveUsers] = useState<GuidanceUser[]>([]);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedGuidanceUser, setSelectedGuidanceUser] = useState<GuidanceUser | null>(null);
  const [highSchools, setHighSchools] = useState<HighSchool[]>([]);
  const [loadingHighSchools, setLoadingHighSchools] = useState(false);
  const [guidanceFormData, setGuidanceFormData] = useState<ApprovalFormData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    highSchool: null,
    avatarFile: null,
  });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [guidanceSubmitting, setGuidanceSubmitting] = useState(false);
  const [guidanceUploadProgress, setGuidanceUploadProgress] = useState(0);
  const [guidanceSuccessMessage, setGuidanceSuccessMessage] = useState('');
  const [showGuidanceSuccessSnackbar, setShowGuidanceSuccessSnackbar] = useState(false);

  // Ai Chats tab state
  const [aiChatsLoading, setAiChatsLoading] = useState(true);
  const [aiChatsError, setAiChatsError] = useState<string | null>(null);
  const [aiChatThreads, setAiChatThreads] = useState<any[]>([]);
  const [selectedThread, setSelectedThread] = useState<any | null>(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [threadMessages, setThreadMessages] = useState<any[]>([]);

  // Firebase Functions setup
  const functions = getFunctions();
  const findUpcomingEvents = httpsCallable(functions, 'findUpcomingEventsForAllSchools');
  const getBulkSearchProgress = httpsCallable(functions, 'getBulkSearchProgress');
  const getAIGeneratedEvents = httpsCallable(functions, 'getAIGeneratedEvents');
  const approveAIGeneratedEvent = httpsCallable(functions, 'approveAIGeneratedEvent');
  const rejectAIGeneratedEvent = httpsCallable(functions, 'rejectAIGeneratedEvent');
  const getSchoolsWithDraftEvents = httpsCallable(functions, 'getSchoolsWithDraftEvents');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    if (newValue === 1 && eventsTabIndex === 1) {
      // When switching to Events tab and Draft AI Events sub-tab, load schools with drafts
      loadSchoolsWithDrafts();
    }
  };

  const handleEventsTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setEventsTabIndex(newValue);
    if (newValue === 1) {
      // When switching to Draft AI Events tab, load schools with drafts
      loadSchoolsWithDrafts();
    }
  };

  // Fetch all tickets from Firebase
  useEffect(() => {
    const feedBackRef = ref(database, 'feedBack');
    
    const unsubscribe = onValue(feedBackRef, (snapshot) => {
      if (!snapshot.exists()) {
        setTickets([]);
        setLoading(false);
        return;
      }

      const allEntries: SupportTicketEntry[] = [];
      snapshot.forEach((childSnapshot) => {
        const entry = childSnapshot.val();
        // Debug: log entry structure to see what we're getting
        console.log('Support ticket entry:', entry);
        allEntries.push({
          ...entry,
          id: childSnapshot.key || undefined,
        });
      });

      // Group entries by supportTicketID
      const groupedMap = new Map<string, SupportTicketEntry[]>();
      allEntries.forEach((entry) => {
        const ticketID = entry.supportTicketID;
        // Skip entries without a valid supportTicketID
        if (!ticketID || ticketID.trim() === '') {
          console.warn('Entry missing supportTicketID, skipping:', entry);
          return;
        }
        if (!groupedMap.has(ticketID)) {
          groupedMap.set(ticketID, []);
        }
        groupedMap.get(ticketID)!.push(entry);
      });

      // Convert to array and process
      const grouped: GroupedTicket[] = Array.from(groupedMap.entries())
        .map(([ticketID, entries]) => {
          // Skip if no entries
          if (!entries || entries.length === 0) {
            console.warn('Ticket has no entries:', ticketID);
            return null;
          }
          
          // Sort entries by timestamp (oldest first)
          entries.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
          
          // Get the latest entry for status check
          const latestEntry = entries[entries.length - 1];
          const firstEntry = entries[0];
          
          const groupedTicket = {
            supportTicketID: ticketID,
            entries,
            clientUserID: firstEntry?.clientUserID || '',
            latestStatus: latestEntry?.status || '',
            latestTimestamp: latestEntry?.timestamp || 0,
            firstComment: firstEntry?.comment || '',
          };
          
          // Debug: log grouped ticket to see what we have
          console.log('Grouped ticket:', groupedTicket);
          
          return groupedTicket;
        })
        // Filter out null tickets
        .filter((ticket): ticket is GroupedTicket => ticket !== null)
        // Sort by latest timestamp (newest first)
        .sort((a, b) => b.latestTimestamp - a.latestTimestamp);

      setTickets(grouped);
      setLoading(false);

      // Fetch user data for all unique clientUserIDs
      const uniqueUserIds = new Set(grouped.map(t => t.clientUserID));
      fetchUserData(Array.from(uniqueUserIds));
    }, (error) => {
      console.error('Error fetching support tickets:', error);
      setError('Failed to load support tickets. Please try again.');
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Fetch guidance users
  useEffect(() => {
    if (currentTab === 2) {
      const fetchGuidanceUsers = async () => {
        setGuidanceLoading(true);
        setGuidanceError(null);

        try {
          const adminGetGuidanceUsers = httpsCallable(functions, 'adminGetGuidanceUsers');
          const result = await adminGetGuidanceUsers();

          const data = result.data as {
            success: boolean;
            pendingUsers: GuidanceUser[];
            activeUsers: GuidanceUser[];
          };

          if (data.success) {
            setPendingUsers(data.pendingUsers || []);
            setActiveUsers(data.activeUsers || []);
          } else {
            setGuidanceError('Failed to load users. Please try again.');
          }
        } catch (err: any) {
          console.error('Error fetching guidance users:', err);
          setGuidanceError(err.message || 'Failed to load users. Please try again.');
        } finally {
          setGuidanceLoading(false);
        }
      };

      fetchGuidanceUsers();
    }
  }, [currentTab]);

  // Fetch user data from Firebase
  const fetchUserData = async (userIds: string[]) => {
    const userDataPromises = userIds.map(async (userId) => {
      try {
        const userRef = ref(database, `user/${userId}`);
        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          const userData = snapshot.val();
          const profileData = userData.ProfileData || {};
          return {
            userId,
            data: {
              firstName: profileData['First Name'] || '',
              lastName: profileData['Last Name'] || '',
              email: profileData.Email || '',
            },
          };
        }
      } catch (err) {
        console.error(`Error fetching user data for ${userId}:`, err);
      }
      return null;
    });

    const results = await Promise.all(userDataPromises);
    const userMap: Record<string, { firstName?: string; lastName?: string; email?: string }> = {};
    results.forEach((result) => {
      if (result) {
        userMap[result.userId] = result.data;
      }
    });
    setUserDataMap((prev) => ({ ...prev, ...userMap }));
  };

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      // UserID filter
      if (userIdFilter && !ticket.clientUserID.toLowerCase().includes(userIdFilter.toLowerCase())) {
        const userName = `${userDataMap[ticket.clientUserID]?.firstName || ''} ${userDataMap[ticket.clientUserID]?.lastName || ''}`.toLowerCase();
        const userEmail = userDataMap[ticket.clientUserID]?.email?.toLowerCase() || '';
        if (!userName.includes(userIdFilter.toLowerCase()) && !userEmail.includes(userIdFilter.toLowerCase())) {
          return false;
        }
      }

      // TicketID filter
      if (ticketIdFilter && !ticket.supportTicketID.toLowerCase().includes(ticketIdFilter.toLowerCase())) {
        return false;
      }

      // Status filter
      if (statusFilter && statusFilter !== 'Show All' && ticket.latestStatus !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [tickets, userIdFilter, ticketIdFilter, statusFilter, userDataMap]);

  // Get unique statuses for filter dropdown
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(tickets.map(t => t.latestStatus));
    return Array.from(statuses).sort();
  }, [tickets]);

  const handleTicketClick = (ticket: GroupedTicket) => {
    setSelectedTicket(ticket);
    setResponseComment('');
    setResponseStatus('Resolution in Process');
    setResponseDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setResponseDialogOpen(false);
    setSelectedTicket(null);
    setResponseComment('');
    setResponseStatus('Resolution in Process');
  };

  const handleSubmitResponse = async () => {
    if (!selectedTicket || !userData?.uid || !responseComment.trim()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const feedBackRef = ref(database, 'feedBack');
      const newEntryRef = push(feedBackRef);

      const responseEntry: Omit<SupportTicketEntry, 'id'> = {
        supportTicketID: selectedTicket.supportTicketID,
        clientUserID: selectedTicket.clientUserID,
        responderUserID: userData.uid,
        timestamp: Date.now(),
        comment: responseComment.trim(),
        status: responseStatus,
      };

      await set(newEntryRef, responseEntry);

      setSuccessMessage(true);
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting response:', error);
      setError('Failed to submit response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted':
        return 'info';
      case 'resolution in process':
        return 'warning';
      case 'bug identified':
        return 'error';
      case 'feature enhancement':
        return 'primary';
      case 'clarification requested':
        return 'secondary';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getUserDisplayName = (userId: string) => {
    const user = userDataMap[userId];
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.email) {
      return user.email;
    }
    return userId;
  };

  // Format EdVisor message content with icons, links, etc.
  const formatEdvisorMessage = (messageContent: string): string => {
    if (!messageContent) return '';
    
    // Parse the message content to get HTML with formatted links, chips, etc.
    const parsedHTML = parseMessageContentAsHTML(
      messageContent,
      programs,
      undefined, // userGrade - not needed for admin view
      undefined, // userCourses - not needed for admin view
      undefined, // prerequisites - not needed for admin view
      schools
    );
    
    // Replace eligibility icon placeholders with [icon] text
    const eligibilityIconRegex = /__REACT_ELIGIBILITY_ICON_[^_]+(?:_[^_]+)*__/g;
    const formattedHTML = parsedHTML.replace(eligibilityIconRegex, '[icon]');
    
    return formattedHTML;
  };

  // Events tab helper functions and computed values
  const availableProgramCategories = useMemo(() => {
    const categoryCounts: { [category: string]: number } = {};
    programs.forEach(program => {
      if (program.category && program.category.trim() !== '') {
        categoryCounts[program.category] = (categoryCounts[program.category] || 0) + 1;
      }
    });
    return Object.keys(categoryCounts)
      .filter(category => categoryCounts[category] > 0)
      .sort();
  }, [programs]);

  const additionalEventCategories = [
    'Admissions, Applications, Offers',
    'Clubs',
    'Financial Aid',
    'General',
    'Residence/Meal Plans',
    'Scholarships'
  ];

  const allEventCategories = useMemo(() => {
    const combined = [...availableProgramCategories, ...additionalEventCategories];
    return combined.sort();
  }, [availableProgramCategories]);

  const filteredEventsByMode = useMemo(() => {
    if (filterMode === 'all') {
      return events;
    }
    const now = new Date();
    return events.filter(event => {
      if (!event.startTime) return false;
      try {
        const eventDate = new Date(event.startTime);
        return eventDate > now;
      } catch {
        return false;
      }
    });
  }, [events, filterMode]);

  const uniqueHosts = useMemo(() => {
    const hosts = filteredEventsByMode
      .map(event => event.hostName || event.host || '')
      .filter(host => host.trim() !== '')
      .filter((host, index, array) => array.indexOf(host) === index)
      .sort((a, b) => a.localeCompare(b));
    return hosts;
  }, [filteredEventsByMode]);

  const allSchoolNames = useMemo(() => {
    const schoolNames = schools.map(school => school.name);
    const uniqueNames = Array.from(new Set(schoolNames));
    return uniqueNames.sort((a, b) => a.localeCompare(b));
  }, [schools]);

  React.useEffect(() => {
    if (uniqueHosts.length > 0 && !selectedHost) {
      setSelectedHost(uniqueHosts[0]);
    }
  }, [uniqueHosts, selectedHost]);

  const filteredEvents = useMemo(() => {
    if (!selectedHost) return [];
    return filteredEventsByMode
      .filter(event => (event.hostName || event.host || '') === selectedHost)
      .sort((a, b) => {
        const dateA = new Date(a.startTime || '');
        const dateB = new Date(b.startTime || '');
        return dateA.getTime() - dateB.getTime();
      });
  }, [filteredEventsByMode, selectedHost]);

  const filteredEventsForDraftSchool = useMemo(() => {
    if (!selectedDraftSchool) return [];
    const selectedSchool = schoolsWithDrafts.find(s => s.id === selectedDraftSchool);
    if (!selectedSchool) return [];
    return filteredEventsByMode
      .filter(event => {
        const eventHost = (event.hostName || event.host || '').toLowerCase();
        const selectedSchoolName = selectedSchool.name.toLowerCase();
        return eventHost === selectedSchoolName;
      })
      .sort((a, b) => {
        const dateA = new Date(a.startTime || '');
        const dateB = new Date(b.startTime || '');
        return dateA.getTime() - dateB.getTime();
      });
  }, [filteredEventsByMode, selectedDraftSchool, schoolsWithDrafts]);

  const filteredAiEvents = useMemo(() => {
    return aiGeneratedEvents.filter(aiEvent => {
      const status = aiEvent.draftStatus || 'pending';
      const statusMap: { [key: string]: string } = {
        'pending': 'pending',
        'approved': 'approved', 
        'rejected': 'rejected',
        '': 'pending'
      };
      const mappedStatus = statusMap[status] || 'pending';
      return selectedStatuses.includes(mappedStatus);
    });
  }, [aiGeneratedEvents, selectedStatuses]);

  const canadianSchools = useMemo(() => {
    if (!schools || schools.length === 0) {
      return [];
    }
    const filtered = schools
      .filter(school => {
        const hasWebsite = school.website && school.website.trim() !== '';
        return hasWebsite;
      })
      .map(school => ({
        id: school.id,
        name: school.name,
        website: school.website,
        city: school.city,
        province: school.province
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return filtered;
  }, [schools]);

  const formatDate = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return dateTimeString;
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateTimeString;
    }
  };

  const formatTime = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return dateTimeString;
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${month}/${day}/${year} (${displayHours}:${displayMinutes}${ampm})`;
    } catch {
      return dateTimeString;
    }
  };

  const parseDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: '', time: '' };
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return { date: '', time: '' };
      const dateStr = date.toISOString().split('T')[0];
      const timeStr = date.toTimeString().slice(0, 5);
      return { date: dateStr, time: timeStr };
    } catch {
      return { date: '', time: '' };
    }
  };

  const handleHostChange = (event: any) => {
    setSelectedHost(event.target.value);
  };

  const handleFilterModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilterMode: 'future' | 'all' | null,
  ) => {
    if (newFilterMode !== null) {
      setFilterMode(newFilterMode);
      setSelectedHost('');
    }
  };

  const handleEditClick = (event: any) => {
    setEditingEvent(event);
    setIsCreateMode(false);
    let eventData = event;
    if (event.eventContent && event.eventContent.length > 0) {
      eventData = event.eventContent[0];
    }
    const startDateTime = parseDateTime(eventData.startTime || '');
    const endDateTime = parseDateTime(eventData.endTime || '');
    setEditForm({
      name: eventData.title || eventData.name || '',
      description: eventData.description || '',
      location: eventData.address || eventData.location || '',
      startDate: startDateTime.date,
      startTime: startDateTime.time,
      endDate: endDateTime.date,
      endTime: endDateTime.time,
      website: eventData.website || '',
      registerRequired: eventData.registerRequired || false,
      eventType: eventData.eventType || 'physical',
      private: eventData.private || false,
      hostName: eventData.hostName || eventData.host || event.schoolName || '',
      eventCategory: Array.isArray(eventData.eventCategory) 
        ? eventData.eventCategory 
        : eventData.eventCategory 
          ? [eventData.eventCategory] 
          : ['General']
    });
    setEditModalOpen(true);
    setSaveError(null);
  };

  const handleCreateClick = () => {
    setEditingEvent(null);
    setIsCreateMode(true);
    setEditForm({
      name: '',
      description: '',
      location: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      website: '',
      registerRequired: false,
      eventType: 'physical',
      private: false,
      hostName: selectedHost || '',
      eventCategory: ['General']
    });
    setEditModalOpen(true);
    setSaveError(null);
  };

  const handleEditFormChange = (field: string, value: string | boolean | string[]) => {
    setEditForm(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };
      if (field === 'startDate' && typeof value === 'string' && value && !prev.endDate) {
        const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
        if (dateRegex.test(value)) {
          updatedData.endDate = value;
        }
      }
      if (field === 'startTime' && typeof value === 'string' && value && !prev.endTime) {
        let timeValue = value.trim();
        let hours = 0;
        let minutes = 0;
        if (timeValue.includes('AM') || timeValue.includes('PM')) {
          const isPM = timeValue.includes('PM');
          const timePart = timeValue.replace(/\s*(AM|PM)/i, '').trim();
          const [hourStr, minuteStr] = timePart.split(':');
          hours = parseInt(hourStr, 10);
          minutes = parseInt(minuteStr || '0', 10);
          if (isPM && hours !== 12) {
            hours += 12;
          } else if (!isPM && hours === 12) {
            hours = 0;
          }
        } else {
          const [hourStr, minuteStr] = timeValue.split(':');
          hours = parseInt(hourStr, 10);
          minutes = parseInt(minuteStr || '0', 10);
        }
        hours += 1;
        if (hours >= 24) {
          hours = 0;
        }
        updatedData.endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
      if (field === 'eventType' && value === 'virtual') {
        updatedData.location = 'Virtual';
      }
      if (field === 'eventType' && value !== 'physical' && value !== 'hybrid' && value !== 'virtual') {
        updatedData.location = '';
      }
      return updatedData;
    });
  };

  const handleSave = async () => {
    if (!userData?.uid) {
      setSaveError('User authentication required');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const startDateTime = editForm.startDate && editForm.startTime 
        ? `${editForm.startDate} ${editForm.startTime}` 
        : '';
      const endDateTime = editForm.endDate && editForm.endTime
        ? `${editForm.endDate} ${editForm.endTime}`
        : '';
      if (editForm.eventType === 'hybrid' && !editForm.website.trim()) {
        throw new Error('Website is required for Hybrid Events');
      }
      const eventContent = {
        title: editForm.name,
        description: editForm.description,
        address: editForm.location,
        startTime: startDateTime,
        endTime: endDateTime,
        website: editForm.website,
        registerRequired: editForm.registerRequired,
        eventType: editForm.eventType,
        private: editForm.private,
        hostName: editForm.hostName,
        eventCategory: editForm.eventCategory.length > 0 ? editForm.eventCategory : ['General']
      };
      if (isCreateMode) {
        if (!editForm.hostName || editForm.hostName.trim() === '') {
          throw new Error('Host name is required');
        }
        const schoolId = await findSchoolIdByName(editForm.hostName);
        if (!schoolId) {
          throw new Error(`School not found: ${editForm.hostName}`);
        }
        const eventData = {
          eventContent,
          postTime: Date.now(),
        };
        const eventsRef = ref(database, `schoolEvent/${schoolId}/${userData.uid}`);
        const newEventRef = push(eventsRef);
        await set(newEventRef, eventData);
      } else {
        if (!editingEvent) {
          setSaveError('Event information is missing');
          return;
        }
        if (editingEvent.eventContent && editingEvent.eventContent.length > 0) {
          const schoolId = editingEvent.schoolId || selectedDraftSchool;
          if (!schoolId) {
            setSaveError('School ID is missing for AI draft event');
            return;
          }
          await approveAIGeneratedEvent({
            schoolId: schoolId,
            eventId: editingEvent.id,
            eventData: eventContent
          });
        } else {
          if (!editingEvent.schoolId || !editingEvent.userId) {
            setSaveError('Event information is missing');
            return;
          }
          const eventRef = ref(database, `schoolEvent/${editingEvent.schoolId}/${editingEvent.userId}/${editingEvent.id}/eventContent`);
          await update(eventRef, eventContent);
        }
      }
      refreshEvents();
      if (editingEvent && editingEvent.eventContent && editingEvent.eventContent.length > 0) {
        if (selectedDraftSchool) {
          loadAIGeneratedEvents(selectedDraftSchool);
        }
        loadSchoolsWithDrafts();
      }
      setEditModalOpen(false);
      setEditingEvent(null);
      setIsCreateMode(false);
    } catch (error) {
      console.error('Error saving event:', error);
      setSaveError('Failed to save event. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditModalOpen(false);
    setEditingEvent(null);
    setIsCreateMode(false);
    setSaveError(null);
  };

  const loadSchoolsWithDrafts = async () => {
    try {
      setAiEventsLoading(true);
      const result = await getSchoolsWithDraftEvents();
      setSchoolsWithDrafts((result.data as any).schools || []);
    } catch (error) {
      console.error('Error loading schools with drafts:', error);
      setSchoolsWithDrafts([]);
    } finally {
      setAiEventsLoading(false);
    }
  };

  const handleFindNewEvents = async () => {
    try {
      setAiSearchInProgress(true);
      setSearchProgress(null);
      const searchParams = selectedDraftTabSchool ? { schoolName: selectedDraftTabSchool } : {};
      const result = await findUpcomingEvents(searchParams);
      const searchId = (result.data as any).searchId;
      setSearchId(searchId);
      pollSearchProgress(searchId);
    } catch (error) {
      console.error('Error starting AI search:', error);
      setAiSearchInProgress(false);
    }
  };

  const pollSearchProgress = async (searchId: string) => {
    try {
      const result = await getBulkSearchProgress({ searchId });
      const progress = (result.data as any).progress;
      setSearchProgress(progress);
      if (progress.status === 'running') {
        setTimeout(() => pollSearchProgress(searchId), 2000);
      } else if (progress.status === 'completed') {
        setAiSearchInProgress(false);
        setSearchSummary(progress);
        loadSchoolsWithDrafts();
      } else if (progress.status === 'failed') {
        setAiSearchInProgress(false);
        console.error('AI search failed:', progress.error);
      }
    } catch (error) {
      console.error('Error polling search progress:', error);
      setAiSearchInProgress(false);
    }
  };

  const loadAIGeneratedEvents = async (schoolId: string) => {
    try {
      setAiEventsLoading(true);
      const result = await getAIGeneratedEvents({ schoolId });
      setAiGeneratedEvents((result.data as any).events || []);
    } catch (error) {
      console.error('Error loading AI generated events:', error);
      setAiGeneratedEvents([]);
    } finally {
      setAiEventsLoading(false);
    }
  };

  const handleDraftSchoolChange = (schoolId: string) => {
    setSelectedDraftSchool(schoolId);
    if (schoolId) {
      loadAIGeneratedEvents(schoolId);
    } else {
      setAiGeneratedEvents([]);
    }
  };

  const handleStatusFilterChange = (event: any) => {
    const value = event.target.value;
    setSelectedStatuses(typeof value === 'string' ? value.split(',') : value);
  };

  const handleRejectAIDraftEvent = async (eventId: string, schoolId: string) => {
    try {
      await rejectAIGeneratedEvent({
        schoolId: schoolId,
        eventId: eventId,
        reason: 'Rejected by user review'
      });
      if (selectedDraftSchool) {
        loadAIGeneratedEvents(selectedDraftSchool);
      }
      loadSchoolsWithDrafts();
    } catch (error) {
      console.error('Error rejecting AI draft event:', error);
    }
  };

  const handleDraftTabSchoolChange = (schoolName: string) => {
    setSelectedDraftTabSchool(schoolName);
  };

  // Guidance tab helper functions
  const handleGuidanceTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setGuidanceTabIndex(newValue);
  };

  const fetchHighSchools = async () => {
    setLoadingHighSchools(true);
    try {
      const schoolsRef = ref(database, 'highSchools');
      const snapshot = await get(schoolsRef);
      if (snapshot.exists()) {
        const schoolsData = snapshot.val();
        const schoolsList: HighSchool[] = Object.entries(schoolsData).map(([id, data]: [string, any]) => ({
          id,
          name: data.HighSchoolName || data.SchoolName || '',
        })).filter((school: HighSchool) => school.name)
          .sort((a: HighSchool, b: HighSchool) => a.name.localeCompare(b.name));
        setHighSchools(schoolsList);
      }
    } catch (error) {
      console.error('Error fetching high schools:', error);
    } finally {
      setLoadingHighSchools(false);
    }
  };

  const handleApproveClick = (user: GuidanceUser) => {
    setSelectedGuidanceUser(user);
    setGuidanceFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      username: `${user.firstName} ${user.lastName}`.trim(),
      highSchool: null,
      avatarFile: null,
    });
    setAvatarPreview(null);
    setApprovalModalOpen(true);
    fetchHighSchools();
  };

  const handleGuidanceModalClose = () => {
    setApprovalModalOpen(false);
    setSelectedGuidanceUser(null);
    setGuidanceFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      highSchool: null,
      avatarFile: null,
    });
    setAvatarPreview(null);
    setGuidanceUploadProgress(0);
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setGuidanceError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setGuidanceError('Image size must be less than 5MB');
        return;
      }
      setGuidanceFormData({ ...guidanceFormData, avatarFile: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGuidanceSubmit = async () => {
    if (!selectedGuidanceUser) return;

    if (!guidanceFormData.highSchool) {
      setGuidanceError('Please select a high school');
      return;
    }
    if (!guidanceFormData.avatarFile) {
      setGuidanceError('Please select an avatar image');
      return;
    }

    setGuidanceSubmitting(true);
    setGuidanceError(null);
    setGuidanceUploadProgress(0);

    try {
      const avatarPath = `schoolUserLogo/hsCounselor_schoolIcon/${selectedGuidanceUser.uid}.jpg`;
      const avatarStorageRef = storageRef(storage, avatarPath);
      
      const progressInterval = setInterval(() => {
        setGuidanceUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      await uploadBytes(avatarStorageRef, guidanceFormData.avatarFile);
      clearInterval(progressInterval);
      setGuidanceUploadProgress(90);

      const downloadURL = await getDownloadURL(avatarStorageRef);
      setGuidanceUploadProgress(100);

      const highSchoolUserRef = ref(database, `highSchoolUser/${selectedGuidanceUser.uid}`);
      await set(highSchoolUserRef, {
        account: guidanceFormData.email,
        schoolID: guidanceFormData.highSchool.id,
        schoolIcon: downloadURL,
        schoolName: guidanceFormData.highSchool.name,
        username: guidanceFormData.username,
      });

      // Refresh users data
      const adminGetGuidanceUsers = httpsCallable(functions, 'adminGetGuidanceUsers');
      const result = await adminGetGuidanceUsers();
      const data = result.data as {
        success: boolean;
        pendingUsers: GuidanceUser[];
        activeUsers: GuidanceUser[];
      };

      if (data.success) {
        setPendingUsers(data.pendingUsers || []);
        setActiveUsers(data.activeUsers || []);
      }

      setGuidanceSuccessMessage('Guidance user approved successfully!');
      setShowGuidanceSuccessSnackbar(true);
      handleGuidanceModalClose();
      
      setGuidanceTabIndex(1);
    } catch (err: any) {
      console.error('Error approving user:', err);
      setGuidanceError(err.message || 'Failed to approve user. Please try again.');
    } finally {
      setGuidanceSubmitting(false);
      setGuidanceUploadProgress(0);
    }
  };

  const renderGuidanceUserTable = (users: GuidanceUser[], showApproveButton: boolean = false) => {
    if (users.length === 0) {
      return (
        <Alert severity="info">
          No users found in this category.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>UID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>First Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Last Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Email / Phone</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>High School</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>City</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Province</TableCell>
              <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Self Claimed Guidance</TableCell>
              {showApproveButton && <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.uid} hover>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                    {user.uid}
                  </Typography>
                </TableCell>
                <TableCell>{user.firstName || '-'}</TableCell>
                <TableCell>{user.lastName || '-'}</TableCell>
                <TableCell>{user.email || '-'}</TableCell>
                <TableCell>{user.highSchool || '-'}</TableCell>
                <TableCell>{user.city || '-'}</TableCell>
                <TableCell>{user.province || '-'}</TableCell>
                <TableCell>{user.selfClaimedGuidance ? 'Yes' : 'No'}</TableCell>
                {showApproveButton && (
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleApproveClick(user)}
                    >
                      Approve
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Fetch AI Chats data
  useEffect(() => {
    if (currentTab === 3) {
      const fetchAiChats = async () => {
        setAiChatsLoading(true);
        setAiChatsError(null);
        try {
          const adminGetAiChats = httpsCallable(functions, 'adminGetAiChats');
          const result = await adminGetAiChats();

          const data = result.data as {
            success: boolean;
            threads: any[];
          };

          if (data.success) {
            setAiChatThreads(data.threads || []);
          } else {
            setAiChatsError('Failed to load AI chats. Please try again.');
          }
        } catch (err: any) {
          console.error('Error fetching AI chats:', err);
          setAiChatsError(err.message || 'Failed to load AI chats. Please try again.');
        } finally {
          setAiChatsLoading(false);
        }
      };

      fetchAiChats();
    }
  }, [currentTab]);

  // Get page title based on selected tab
  const getPageTitle = () => {
    switch (currentTab) {
      case 0:
        return 'Admin: Support';
      case 1:
        return 'Admin: Events';
      case 2:
        return 'Admin: Guidance';
      case 3:
        return 'Admin: Ai Chats';
      case 4:
        return 'Admin: Reports';
      default:
        return 'Admin: Support';
    }
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        {getPageTitle()}
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="admin support tabs">
          <Tab label="Support" />
          <Tab label="Events" />
          <Tab label="Guidance" />
          <Tab label="Ai Chats" />
          <Tab label="Reports" />
        </Tabs>
      </Box>

      {/* Support Tab Content */}
      {currentTab === 0 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Filter by User ID, Name, or Email"
                    value={userIdFilter}
                    onChange={(e) => setUserIdFilter(e.target.value)}
                    placeholder="Search users..."
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Filter by Ticket ID"
                    value={ticketIdFilter}
                    onChange={(e) => setTicketIdFilter(e.target.value)}
                    placeholder="Search ticket ID..."
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Filter by Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="Show All">Show All</MenuItem>
                      {uniqueStatuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Results Summary */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
          </Typography>

          {/* Tickets Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : filteredTickets.length === 0 ? (
            <Alert severity="info">
              No support tickets found matching the filters.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Latest Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>First Comment</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ticket ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket.supportTicketID} hover>
                      <TableCell>
                        <Chip
                          label={ticket.latestStatus}
                          color={getStatusColor(ticket.latestStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getUserDisplayName(ticket.clientUserID)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ticket.clientUserID}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.firstComment}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {ticket.supportTicketID}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatTimestamp(ticket.latestTimestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleTicketClick(ticket)}
                        >
                          Respond
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Events Tab Content */}
      {currentTab === 1 && (
        <Box>
          {eventsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : eventsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {eventsError}
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">
                  Add / Edit Events
                </Typography>
                {eventsTabIndex === 0 && (
                  <ToggleButtonGroup
                    value={filterMode}
                    exclusive
                    onChange={handleFilterModeChange}
                    aria-label="filter mode"
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        border: '1px solid #e0e0e0',
                        color: '#666',
                        backgroundColor: '#fff',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#1976d2',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#1565c0',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="future" aria-label="future events">
                      Future Events
                    </ToggleButton>
                    <ToggleButton value="all" aria-label="show all">
                      Show All
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>

              {/* Events Sub-tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={eventsTabIndex} onChange={handleEventsTabChange} aria-label="events tabs">
                  <Tab label="Add/Edit Events" />
                  <Tab 
                    label={
                      <Badge badgeContent={schoolsWithDrafts.length} color="primary">
                        Draft AI Events
                      </Badge>
                    } 
                  />
                </Tabs>
              </Box>

              {/* Add/Edit Events Sub-tab */}
              {eventsTabIndex === 0 && (
                <Box>
                  {/* Host Selection Dropdown */}
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel id="host-select-label">Select Host / School:</InputLabel>
                      <Select
                        labelId="host-select-label"
                        id="host-select"
                        value={selectedHost}
                        label="Select Host / School:"
                        onChange={handleHostChange}
                      >
                        {uniqueHosts.map((host) => (
                          <MenuItem key={host} value={host}>
                            {host}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Events Table */}
                  {selectedHost && (
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          Events for {selectedHost}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleCreateClick}
                          sx={{ 
                            backgroundColor: '#1976d2',
                            '&:hover': {
                              backgroundColor: '#1565c0',
                            },
                          }}
                        >
                          + Create a New Event
                        </Button>
                      </Box>
                      
                      {filteredEvents.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Event Title</strong></TableCell>
                                <TableCell><strong>Location</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredEvents.map((event) => (
                                <TableRow key={event.id}>
                                  <TableCell>
                                    {formatDate(event.startTime || '')} {formatTime(event.startTime || '')}
                                  </TableCell>
                                  <TableCell>{event.title || event.name || ''}</TableCell>
                                  <TableCell>{event.address || event.location || ''}</TableCell>
                                  <TableCell>
                                    <IconButton
                                      onClick={() => handleEditClick(event)}
                                      color="primary"
                                      size="small"
                                      title="Edit Event"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                          No events found for {selectedHost}.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Draft AI Events Sub-tab */}
              {eventsTabIndex === 1 && (
                <Box>
                  {/* School Selection for AI Search */}
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel id="draft-tab-school-select-label">Select School / Host:</InputLabel>
                      <Select
                        labelId="draft-tab-school-select-label"
                        id="draft-tab-school-select"
                        value={selectedDraftTabSchool}
                        label="Select School / Host:"
                        onChange={(e) => handleDraftTabSchoolChange(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>All Canadian Schools (Random 3)</em>
                        </MenuItem>
                        <MenuItem value="ALL_SCHOOLS">
                          <em>All Schools (Production Mode)</em>
                        </MenuItem>
                        {canadianSchools.map((school) => (
                          <MenuItem key={school.id} value={school.name}>
                            <Box>
                              <Typography variant="body1">
                                {school.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {school.city}, {school.province}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Find New Events Button */}
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleFindNewEvents}
                      disabled={aiSearchInProgress}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        },
                        minWidth: 200,
                      }}
                    >
                      {aiSearchInProgress ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Searching...
                        </>
                      ) : (
                        'Find New Events'
                      )}
                    </Button>
                  </Box>

                  {/* Search Progress */}
                  {searchProgress && searchProgress.status === 'running' && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Search Progress
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ mr: 2 }}>
                          {searchProgress.currentSchool || 'Starting search...'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({searchProgress.schoolsProcessed}/{searchProgress.totalSchools} schools)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(searchProgress.schoolsProcessed / searchProgress.totalSchools) * 100}
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Events found: {searchProgress.eventsFound}
                      </Typography>
                    </Box>
                  )}

                  {/* Search Summary */}
                  {searchSummary && searchSummary.status === 'completed' && (
                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                      <Typography variant="h6" gutterBottom color="success.main">
                        ✅ Search Completed Successfully!
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Total Events Found:</strong> {searchSummary.eventsFound}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Schools Processed:</strong> {searchSummary.schoolsProcessed || searchSummary.totalSchools || 0}/{searchSummary.totalSchools || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                      {searchSummary.selectedSchools && searchSummary.selectedSchools.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Schools Selected for Testing:
                          </Typography>
                          {searchSummary.selectedSchools.map((school: any, index: number) => (
                            <Chip
                              key={index}
                              label={`${school.name} (${school.city}, ${school.province})`}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                              color={school.type === 'canadian' ? 'primary' : 'secondary'}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* School Selection for Drafts */}
                  {schoolsWithDrafts.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel id="draft-school-select-label">Select School with Draft Events:</InputLabel>
                        <Select
                          labelId="draft-school-select-label"
                          id="draft-school-select"
                          value={selectedDraftSchool}
                          label="Select School with Draft Events:"
                          onChange={(e) => handleDraftSchoolChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <em>All Schools</em>
                          </MenuItem>
                          {schoolsWithDrafts.map((school) => (
                            <MenuItem key={school.id} value={school.id}>
                              <Box>
                                <Typography variant="body1">
                                  {school.name} ({school.draftCount} drafts)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {school.city}, {school.province}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Status Filter */}
                  {selectedDraftSchool && (
                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel id="status-filter-label">Filter by Status:</InputLabel>
                        <Select
                          labelId="status-filter-label"
                          id="status-filter"
                          multiple
                          value={selectedStatuses}
                          onChange={handleStatusFilterChange}
                          label="Filter by Status:"
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip 
                                  key={value} 
                                  label={value.charAt(0).toUpperCase() + value.slice(1)} 
                                  size="small" 
                                  color={value === 'approved' ? 'success' : value === 'rejected' ? 'error' : 'warning'}
                                />
                              ))}
                            </Box>
                          )}
                        >
                          <MenuItem value="pending">
                            <Checkbox checked={selectedStatuses.indexOf('pending') > -1} />
                            <ListItemText primary="Pending" />
                          </MenuItem>
                          <MenuItem value="approved">
                            <Checkbox checked={selectedStatuses.indexOf('approved') > -1} />
                            <ListItemText primary="Approved" />
                          </MenuItem>
                          <MenuItem value="rejected">
                            <Checkbox checked={selectedStatuses.indexOf('rejected') > -1} />
                            <ListItemText primary="Rejected" />
                          </MenuItem>
                          <MenuItem value="published">
                            <Checkbox checked={selectedStatuses.indexOf('published') > -1} />
                            <ListItemText primary="Published" />
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Combined Events Display */}
                  {selectedDraftSchool && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Events for {schoolsWithDrafts.find(s => s.id === selectedDraftSchool)?.name}
                      </Typography>
                      
                      {aiEventsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Type</strong></TableCell>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Event Title</strong></TableCell>
                                <TableCell><strong>Location</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredEventsForDraftSchool.map((event) => (
                                <TableRow key={event.id}>
                                  <TableCell>
                                    <Chip label="Published" color="success" size="small" />
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(event.startTime || '')} {formatTime(event.startTime || '')}
                                  </TableCell>
                                  <TableCell>{event.title || event.name || ''}</TableCell>
                                  <TableCell>{event.address || event.location || ''}</TableCell>
                                  <TableCell>
                                    <Chip label="Active" color="success" size="small" />
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      onClick={() => handleEditClick(event)}
                                      color="primary"
                                      size="small"
                                      title="Edit Event"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {filteredAiEvents.map((aiEvent) => (
                                <TableRow key={aiEvent.id} sx={{ backgroundColor: '#fff3e0' }}>
                                  <TableCell>
                                    <Box>
                                      <Chip label="AI Draft" color="warning" size="small" />
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        {aiEvent.schoolType === 'canadian' ? '🇨🇦' : '🇺🇸'} {aiEvent.schoolName}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {aiEvent.eventContent && aiEvent.eventContent.length > 0 && 
                                      formatDateTime(aiEvent.eventContent[0].startTime || '')}
                                  </TableCell>
                                  <TableCell>
                                    {aiEvent.eventContent && aiEvent.eventContent.length > 0 && 
                                      aiEvent.eventContent[0].title}
                                  </TableCell>
                                  <TableCell>
                                    {aiEvent.eventContent && aiEvent.eventContent.length > 0 && 
                                      aiEvent.eventContent[0].location}
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={aiEvent.draftStatus || 'Pending'} 
                                      color={aiEvent.draftStatus === 'approved' ? 'success' : 'warning'} 
                                      size="small" 
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <IconButton
                                        onClick={() => handleEditClick(aiEvent)}
                                        color="primary"
                                        size="small"
                                        title="Review AI Event"
                                      >
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton
                                        onClick={() => handleRejectAIDraftEvent(aiEvent.id, aiEvent.schoolId)}
                                        color="error"
                                        size="small"
                                        title="Reject AI Event"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  )}

                  {/* Empty State */}
                  {schoolsWithDrafts.length === 0 && !aiSearchInProgress && (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Draft AI Events Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click "Find New Events" to search all schools for upcoming events using AI.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Edit Event Modal */}
              <Dialog 
                open={editModalOpen} 
                onClose={handleCancel}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>{isCreateMode ? 'Create New Event' : 'Edit Event'}</DialogTitle>
                <DialogContent>
                  {saveError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {saveError}
                    </Alert>
                  )}
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Event Title"
                        value={editForm.name}
                        onChange={(e) => handleEditFormChange('name', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={8}>
                      <Autocomplete
                        freeSolo
                        options={allSchoolNames}
                        value={editForm.hostName}
                        onChange={(event, newValue) => {
                          handleEditFormChange('hostName', newValue || '');
                        }}
                        onInputChange={(event, newInputValue) => {
                          handleEditFormChange('hostName', newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Host Name"
                            placeholder="Select a school or enter custom host name"
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        multiple
                        options={allEventCategories}
                        value={editForm.eventCategory}
                        onChange={(event, newValue) => {
                          handleEditFormChange('eventCategory', newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Event Categories"
                            placeholder="Select categories (optional)"
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              {...getTagProps({ index })}
                              key={option}
                            />
                          ))
                        }
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            {option}
                          </Box>
                        )}
                        isOptionEqualToValue={(option, value) => option === value}
                        noOptionsText="No categories available"
                        freeSolo={false}
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={editForm.description}
                        onChange={(e) => handleEditFormChange('description', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={editForm.startDate}
                        onChange={(e) => handleEditFormChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Time"
                        type="time"
                        value={editForm.startTime}
                        onChange={(e) => handleEditFormChange('startTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={editForm.endDate}
                        onChange={(e) => handleEditFormChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Time"
                        type="time"
                        value={editForm.endTime}
                        onChange={(e) => handleEditFormChange('endTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Event Type</InputLabel>
                        <Select
                          value={editForm.eventType}
                          label="Event Type"
                          onChange={(e) => handleEditFormChange('eventType', e.target.value)}
                        >
                          <MenuItem value="physical">Physical Event</MenuItem>
                          <MenuItem value="virtual">Virtual Event</MenuItem>
                          <MenuItem value="hybrid">Hybrid Event</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {(editForm.eventType === 'physical' || editForm.eventType === 'virtual' || editForm.eventType === 'hybrid') && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Location"
                          value={editForm.location}
                          onChange={(e) => handleEditFormChange('location', e.target.value)}
                          required={editForm.eventType === 'physical' || editForm.eventType === 'hybrid'}
                        />
                      </Grid>
                    )}
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={editForm.eventType === 'hybrid' ? 'Website' : 'Website (optional)'}
                        value={editForm.website}
                        onChange={(e) => handleEditFormChange('website', e.target.value)}
                        placeholder="https://example.com"
                        required={editForm.eventType === 'hybrid'}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editForm.registerRequired}
                            onChange={(e) => handleEditFormChange('registerRequired', e.target.checked)}
                          />
                        }
                        label="Registration Required"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editForm.private}
                            onChange={(e) => handleEditFormChange('private', e.target.checked)}
                          />
                        }
                        label="Private Event"
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCancel} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    disabled={isSaving}
                  >
                    {isSaving ? <CircularProgress size={20} /> : 'Save Changes'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
      )}

      {/* Guidance Tab Content */}
      {currentTab === 2 && (
        <Box>
          {guidanceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : guidanceError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {guidanceError}
            </Alert>
          ) : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={guidanceTabIndex} onChange={handleGuidanceTabChange} aria-label="guidance users tabs">
                  <Tab label={`Pending Guidance (${pendingUsers.length})`} {...a11yProps(0)} />
                  <Tab label={`Active Guidance (${activeUsers.length})`} {...a11yProps(1)} />
                </Tabs>
              </Box>

              <TabPanel value={guidanceTabIndex} index={0}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Pending Guidance Users
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Users who set their User Type to Guidance and are not yet setup with a Guidance account.
                </Typography>
                {renderGuidanceUserTable(pendingUsers, true)}
              </TabPanel>

              <TabPanel value={guidanceTabIndex} index={1}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Active Guidance Users
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Users with Active Guidance access.
                </Typography>
                {renderGuidanceUserTable(activeUsers)}
              </TabPanel>
            </>
          )}

          {/* Approval Modal */}
          <Dialog
            open={approvalModalOpen}
            onClose={handleGuidanceModalClose}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Approve Guidance User</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {guidanceError && (
                  <Alert severity="error" onClose={() => setGuidanceError(null)}>
                    {guidanceError}
                  </Alert>
                )}

                <TextField
                  label="First Name"
                  value={guidanceFormData.firstName}
                  disabled
                  fullWidth
                />
                <TextField
                  label="Last Name"
                  value={guidanceFormData.lastName}
                  disabled
                  fullWidth
                />
                <TextField
                  label="Email"
                  value={guidanceFormData.email}
                  disabled
                  fullWidth
                />
                <TextField
                  label="Username"
                  value={guidanceFormData.username}
                  onChange={(e) => setGuidanceFormData({ ...guidanceFormData, username: e.target.value })}
                  fullWidth
                />
                <Autocomplete
                  options={highSchools}
                  getOptionLabel={(option) => option.name}
                  loading={loadingHighSchools}
                  value={guidanceFormData.highSchool}
                  onChange={(event, newValue) => {
                    setGuidanceFormData({ ...guidanceFormData, highSchool: newValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="High School"
                      required
                    />
                  )}
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Avatar
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="avatar-upload"
                    type="file"
                    onChange={handleAvatarChange}
                  />
                  <label htmlFor="avatar-upload">
                    <Button variant="outlined" component="span" fullWidth>
                      {guidanceFormData.avatarFile ? 'Change Avatar' : 'Select Avatar Image'}
                    </Button>
                  </label>
                  {avatarPreview && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: '4px',
                        }}
                      />
                    </Box>
                  )}
                </Box>
                {guidanceSubmitting && guidanceUploadProgress > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Uploading avatar... {guidanceUploadProgress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={guidanceUploadProgress} />
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleGuidanceModalClose} disabled={guidanceSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleGuidanceSubmit}
                variant="contained"
                disabled={guidanceSubmitting || !guidanceFormData.highSchool || !guidanceFormData.avatarFile}
              >
                {guidanceSubmitting ? 'Submitting...' : 'Approve'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Success Snackbar */}
          <Snackbar
            open={showGuidanceSuccessSnackbar}
            autoHideDuration={3000}
            onClose={() => setShowGuidanceSuccessSnackbar(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setShowGuidanceSuccessSnackbar(false)} severity="success">
              {guidanceSuccessMessage}
            </Alert>
          </Snackbar>
        </Box>
      )}

      {/* Ai Chats Tab Content */}
      {currentTab === 3 && (
        <Box>
          {aiChatsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : aiChatsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {aiChatsError}
            </Alert>
          ) : aiChatThreads.length === 0 ? (
            <Alert severity="info">
              No AI chat threads found.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last, First</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>High School</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Thread Title</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>UserID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last Message</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}># of messages</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aiChatThreads.map((thread) => (
                    <TableRow 
                      key={`${thread.userId}-${thread.threadId}`} 
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedThread(thread);
                        setThreadMessages(thread.messages || []);
                        setChatModalOpen(true);
                      }}
                    >
                      <TableCell>
                        {thread.lastName || thread.firstName 
                          ? `${thread.lastName || ''}, ${thread.firstName || ''}`.trim()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {thread.highSchool || '-'}
                        </Typography>
                        {thread.userType && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {thread.userType}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{thread.title}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                          {thread.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {thread.lastMessageTimestamp 
                            ? (typeof thread.lastMessageTimestamp === 'string'
                                ? new Date(thread.lastMessageTimestamp).toLocaleString()
                                : new Date(thread.lastMessageTimestamp).toLocaleString())
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{thread.messageCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Reports Tab Content */}
      {currentTab === 4 && (
        <Box>
          <Reports />
        </Box>
      )}

      {/* Response Dialog */}
      <Dialog
        open={responseDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Respond to Support Ticket</DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Ticket ID: <strong>{selectedTicket.supportTicketID}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User: <strong>{getUserDisplayName(selectedTicket.clientUserID)}</strong>
                </Typography>
              </Box>

              <Box sx={{ mb: 3, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Ticket History:
                </Typography>
                {selectedTicket.entries.map((entry, index) => (
                  <Box key={entry.id || index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Chip
                        label={entry.status}
                        color={getStatusColor(entry.status) as any}
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(entry.timestamp)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {entry.comment}
                    </Typography>
                    {entry.responderUserID && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Response by: {getUserDisplayName(entry.responderUserID)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Your Response"
                value={responseComment}
                onChange={(e) => setResponseComment(e.target.value)}
                placeholder="Enter your response..."
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={responseStatus}
                  label="Status"
                  onChange={(e) => setResponseStatus(e.target.value)}
                >
                  <MenuItem value="Resolution in Process">Resolution in Process</MenuItem>
                  <MenuItem value="Bug Identified">Bug Identified</MenuItem>
                  <MenuItem value="Feature Enhancement">Feature Enhancement</MenuItem>
                  <MenuItem value="Clarification Requested">Clarification Requested</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitResponse}
            variant="contained"
            disabled={submitting || !responseComment.trim()}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit Response'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(false)} severity="success">
          Response submitted successfully!
        </Alert>
      </Snackbar>

      {/* Chat Messages Modal */}
      <Dialog
        open={chatModalOpen}
        onClose={() => {
          setChatModalOpen(false);
          setSelectedThread(null);
          setThreadMessages([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedThread ? (
            <>
              <Typography variant="h6">{selectedThread.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                User: {selectedThread.lastName || selectedThread.firstName 
                  ? `${selectedThread.lastName || ''}, ${selectedThread.firstName || ''}`.trim()
                  : selectedThread.userId}
              </Typography>
            </>
          ) : 'Chat Messages'}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              maxHeight: 500,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {threadMessages.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No messages in this thread.
              </Typography>
            ) : (
              threadMessages.map((message: any, index: number) => {
                const isEdvisor = message.role === 'assistant';
                const timestamp = message.timestamp 
                  ? (typeof message.timestamp === 'number'
                      ? new Date(message.timestamp).toLocaleString()
                      : new Date(message.timestamp).toLocaleString())
                  : '';

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: isEdvisor ? 'flex-start' : 'flex-end',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        maxWidth: '80%',
                        backgroundColor: isEdvisor 
                          ? theme.palette.grey[100] 
                          : theme.palette.primary.main,
                        color: isEdvisor 
                          ? theme.palette.text.primary 
                          : theme.palette.primary.contrastText,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 1,
                          opacity: 0.8,
                          fontWeight: 'bold',
                        }}
                      >
                        {isEdvisor ? 'EdVisor' : 'User'}
                        {timestamp && ` • ${timestamp}`}
                      </Typography>
                      {isEdvisor ? (
                        <Box
                          sx={{
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            '& *': {
                              maxWidth: '100%',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            },
                            '& .program-chip, & .school-chip, & .category-chip, & .event-chip, & .tour-chip': {
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            },
                            '& li': {
                              marginLeft: '16px',
                              marginBottom: '4px',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            },
                            '& table': {
                              width: '100%',
                              tableLayout: 'fixed',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            },
                            '& pre, & code': {
                              whiteSpace: 'pre-wrap',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              maxWidth: '100%',
                            },
                          }}
                          dangerouslySetInnerHTML={{ __html: formatEdvisorMessage(message.message || '') }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {message.message || ''}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setChatModalOpen(false);
            setSelectedThread(null);
            setThreadMessages([]);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSupport;

